// Global Vue methods -
// not using mixins for performance reasons and because these are pure functions.

import Vue from "vue";
import axios from "axios";
import orderBy from "lodash/orderBy";
import { format, parseISO, parse, isDate, isToday, isValid } from "date-fns";
import screenfull from "screenfull";

/**
 * Sort an array of objects by property name
 * @param {array} array
 * @param {string} property
 * @param {string} sortOrder
 */
Vue.prototype.MgSortByProperty = function(
    array = null,
    property = null,
    sortOrder = "asc"
) {
    if (!array || !property) {
        return [];
    }

    if (!property) {
        return array;
    }

    return orderBy(array, [item => item[property]], [sortOrder]);
};

/**
 * Pass in an ISO string and return formatted string
 * @param {string} isoString
 */
Vue.prototype.MgFormatISODateTime = function(isoString = "", formatType = "") {
    if (!isoString) return "";
    const isoToDateObj = parseISO(isoString);

    if (!isValid(isoToDateObj)) {
        return "";
    }

    if (!formatType || formatType == "date-time") {
        return format(isoToDateObj, "M/dd h:mm a");
    } else if (formatType == "time") {
        return format(isoToDateObj, "h:mm a");
    } else if (formatType == "day-month") {
        return format(isoToDateObj, "EEE, MMMM dd, yyyy");
    } else if (formatType == "fullday-month") {
        return format(isoToDateObj, "EEEE, MMMM dd, yyyy");
    } else if (formatType == "mm-dd-yyyy") {
        return format(isoToDateObj, "yyyy-MM-dd");
    } else {
        return format(isoToDateObj, "MMMM dd, yyyy");
    }
};

/**
 * Pass in a date string like "2020-07-07" and return formatted date
 * @param {string} string
 */
Vue.prototype.MgFormatDateString = function(string = "") {
    if (!string || "string" !== typeof string) return string;

    const date = parse(string, "yyyy-MM-dd", new Date());

    return format(date, "MMMM dd, yyyy");
};

/**
 * Get the current time - return Date object
 */
Vue.prototype.MgRightNow = function() {
    const now = window.MgServerTime ? window.MgServerTime : new Date();
    return now;
};

/**
 * Is a date string / date today?
 * @param {string | date} value
 */
Vue.prototype.MgIsToday = function(value = "") {
    if (!value) return value;

    let date;

    if (isDate(value)) {
        date = value;
    } else if ("string" === typeof value) {
        date = parse(value, "yyyy-MM-dd", Vue.prototype.MgRightNow());
    } else {
        return false;
    }

    return isToday(date);
};

/**
 * Toggle fullscreen mode
 */
Vue.prototype.MgToggleFullScreen = function() {
    return new Promise((resolve, reject) => {
        if (screenfull.isEnabled) {
            return screenfull
                .toggle()
                .then(() => {
                    return resolve();
                })
                .catch(error => {
                    return reject(error);
                });
        } else {
            return reject(new Error("Full screen not allowed"));
        }
    });
};

/**
 * Get video embed code
 */
/**
 * Pass in a youtube/vimeo url like "https://vimeo.com/244471846"
 * Uses oEmbed API: https://oembed.com/
 *
 * Note: Youtube has a dedicated endpoint and not included here because:
 * client side calls to the youtube oembed api are currently not allowed
 *
 * @param {string} url
 */
Vue.prototype.MgGetVideoEmbed = function(url = "") {
    return new Promise((resolve, reject) => {
        // TODO we can support facebook.com as well once this is done: https://matrix-group.atlassian.net/browse/VMP-657
        const validServices = ["vimeo.com"];

        let isUrlValid = false;
        let rejectMessage = "";
        let requestUrl = "";

        for (let index = 0; index < validServices.length; index++) {
            const service = validServices[index];
            if (url.includes(service)) {
                isUrlValid = true;
                break;
            }
        }

        if (!url) {
            rejectMessage = "no url provided";
        } else if (!isUrlValid) {
            rejectMessage = "video url is not from a valid service";
        }

        if (rejectMessage) {
            return reject(rejectMessage);
        }

        if (url.includes("vimeo.com")) {
            requestUrl = `https://vimeo.com/api/oembed.json?url=${url}`;
        }

        axios
            .get(requestUrl)
            .then(response => {
                return resolve(response.data.html);
            })
            .catch(error => {
                return reject(error);
            });
    });
};

export default Vue;
